<template>
    <div>
        <b-card title="Trendyol Ürün Yönetimi">
            <p>Yerel ürün kategorisi ve özellik seçimleri. Kategori ve seçimler ürünün tüm mağaza bağlantıları için ortaktır.</p>
            <b-form @submit.prevent="load(1)">
                <b-row>
                    <b-col
                        v-for="field in textFilters"
                        :key="field.key"
                        cols="12"
                        md="4"
                    >
                        <b-form-group
                            :label="field.label"
                            :label-for="`filter-${field.key}`"
                        >
                            <b-form-input
                                :id="`filter-${field.key}`"
                                :value="filters[field.key]"
                                :type="field.numeric ? 'number' : 'text'"
                                :min="field.numeric ? 1 : null"
                                @input="setFilter(field.key, $event)"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col
                        v-for="field in booleanFilters"
                        :key="field.key"
                        cols="12"
                        md="4"
                    >
                        <b-form-group
                            :label="field.label"
                            :label-for="`filter-${field.key}`"
                        >
                            <b-form-select
                                :id="`filter-${field.key}`"
                                :value="filters[field.key]"
                                :options="[{ value: '', text: 'Tümü' }, { value: '1', text: field.yes }, { value: '0', text: field.no }]"
                                :disabled="field.key === 'missing_required' && !!filters.missing_required_attribute_id"
                                @change="setFilter(field.key, $event)"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col
                        cols="12"
                        md="4"
                    >
                        <b-form-group
                            label="Eksik zorunlu özellik"
                            label-for="filter-missing-required-attribute"
                            description="Seçilen özelliğin zorunlu olduğu ve doldurulmadığı ürünleri getirir."
                        >
                            <b-form-select
                                id="filter-missing-required-attribute"
                                :value="filters.missing_required_attribute_id"
                                :options="requiredAttributeOptions"
                                :disabled="attributesLoading"
                                @change="setFilter('missing_required_attribute_id', $event)"
                            />
                            <small
                                v-if="attributesLoading"
                                role="status"
                            >Özellikler yükleniyor…</small>
                        </b-form-group>
                        <b-alert
                            :show="!!attributesError"
                            variant="danger"
                        >
                            {{ attributesError }}
                            <b-button
                                size="sm"
                                :disabled="attributesLoading"
                                @click="loadRequiredAttributes"
                            >
                                Tekrar dene
                            </b-button>
                        </b-alert>
                    </b-col>
                </b-row>
                <b-button
                    variant="primary"
                    type="submit"
                    :disabled="loading"
                >
                    Getir
                </b-button>
                <b-button
                    variant="outline-secondary"
                    class="ml-1"
                    @click="clear"
                >
                    Temizle
                </b-button>
            </b-form>
        </b-card>
        <b-card v-if="pagination && !error">
            <product-pagination
                :pagination="pagination"
                :disabled="loading"
                @pageClick="load"
            />
        </b-card>
        <b-card>
            <b-alert
                :show="!!error"
                variant="danger"
            >
                {{ error }} <b-button
                    size="sm"
                    @click="load(page)"
                >
                    Tekrar dene
                </b-button>
            </b-alert>
            <b-table
                v-if="!error"
                :items="products"
                :fields="fields"
                :busy="loading"
                responsive
                show-empty
                empty-text="Filtrelere uygun yerel ürün bulunamadı."
            >
                <template #table-busy>
                    <div
                        class="p-2 text-center"
                        role="status"
                    >
                        Ürünler yükleniyor…
                    </div>
                </template>
                <template #cell(category)="{ item }">
                    {{ item.category ? item.category.name : 'Kategori yok' }}
                </template>
                <template #cell(classification_status)="{ value }">
                    {{ labels[value] || value }}
                </template>
                <template #cell(missing_required_attributes_count)="{ item, value }">
                    {{ value }} / {{ item.required_attributes_count }}
                </template>
                <template #cell(batch_errors_count)="{ item, value }">
                    <b-button
                        v-if="value"
                        size="sm"
                        variant="outline-danger"
                        :aria-label="`${item.product_name}: ${value} gönderim hata kaydını göster`"
                        @click="$refs.editor.open(item.id)"
                    >
                        {{ value }} hata kaydı
                    </b-button>
                    <span v-else>Hata kaydı yok</span>
                </template>
                <template #cell(edit)="{ item }">
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="$refs.editor.open(item.id)"
                    >
                        Düzenle
                    </b-button>
                </template>
            </b-table>
            <product-pagination
                v-if="pagination && !error"
                :pagination="pagination"
                :disabled="loading"
                @pageClick="load"
            />
        </b-card>
        <product-editor
            ref="editor"
            @saved="refresh"
        />
    </div>
</template>
<script>
    import { mapState } from 'vuex'
    import {
        BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BAlert, BTable,
    } from 'bootstrap-vue'
    import { classificationLabels, defaultFilters, errorMessage } from '@/store/v2/trendyol/management'
    import ProductPagination from './ProductPagination.vue'
    import ProductEditor from './ProductEditor.vue'

    export default {
        components: {
            BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BAlert, BTable, ProductPagination, ProductEditor,
        },
        data: () => ({
            loading: false,
            error: '',
            attributesLoading: false,
            attributesError: '',
            version: 0,
            timer: null,
            labels: classificationLabels,
            textFilters: [
                { key: 'merchant_sku', label: 'Barkod (tam eşleşme)' },
                { key: 'product_name', label: 'Ürün adı' },
                { key: 'brand_id', label: 'Marka (Trendyol kimliği)', numeric: true },
                { key: 'category_id', label: 'Kategori (Trendyol kimliği)', numeric: true },
                { key: 'store_id', label: 'Mağaza kimliği', numeric: true },
                { key: 'batch_store_id', label: 'Gönderim hatası mağaza kimliği', numeric: true },
            ],
            booleanFilters: [
                {
                    key: 'has_batch_error', label: 'Gönderim hata geçmişi', yes: 'Hata kaydı olan ürünler', no: 'Hata kaydı olmayan ürünler',
                },
                {
                    key: 'has_category', label: 'Kategori durumu', yes: 'Kategorisi var', no: 'Kategori yok',
                },
                {
                    key: 'matched', label: 'Eşleşme durumu', yes: 'Eşleşmiş', no: 'Eşleşmemiş',
                },
                {
                    key: 'in_store', label: 'Mağaza bağlantısı', yes: 'Mağazada bulunan', no: 'Mağazada bulunmayan',
                },
                {
                    key: 'missing_required', label: 'Zorunlu özellik eksikliği', yes: 'Eksik var', no: 'Eksik yok',
                },
            ],
            fields: [
                { key: 'merchant_sku', label: 'Barkod' }, { key: 'product_name', label: 'Ürün adı' },
                { key: 'batch_errors_count', label: 'Gönderim hataları' },
                { key: 'category', label: 'Kategori' }, { key: 'classification_status', label: 'Durum' },
                { key: 'missing_required_attributes_count', label: 'Eksik zorunlu özellik' }, { key: 'edit', label: 'İşlem' },
            ],
        }),
        computed: {
            ...mapState('tyManagement', ['filters', 'products', 'pagination', 'requiredAttributes']),
            requiredAttributeOptions() {
                return [{ value: '', text: 'Tümü' }, ...this.requiredAttributes.map(attribute => ({ value: attribute.attribute_id, text: attribute.name }))]
            },
            page() { return this.pagination ? this.pagination.current_page : 1 },
        },
        created() { this.load(1); this.loadRequiredAttributes() },
        beforeDestroy() {
            clearTimeout(this.timer)
            this.version += 1
            this.$store.commit('tyManagement/invalidateList')
        },
        async beforeRouteLeave(to, from, next) {
            next(await this.$refs.editor.confirmClose() ? undefined : false)
        },
        methods: {
            async loadRequiredAttributes() {
                this.attributesLoading = true
                this.attributesError = ''
                try {
                    await this.$store.dispatch('tyManagement/loadRequiredAttributes')
                } catch (error) {
                    this.attributesError = errorMessage(error)
                } finally {
                    this.attributesLoading = false
                }
            },
            setFilter(key, value) {
                const filters = { ...this.filters, [key]: value }
                if (key === 'missing_required_attribute_id') filters.missing_required = value ? '1' : ''
                this.$store.commit('tyManagement/setFilters', filters)
                this.$store.commit('tyManagement/invalidateList')
                this.version += 1
                clearTimeout(this.timer)
                this.timer = setTimeout(() => this.load(1), 350)
            },
            clear() {
                this.$store.commit('tyManagement/setFilters', defaultFilters())
                this.load(1)
            },
            refresh() { this.load(this.page) },
            async load(page) {
                clearTimeout(this.timer)
                this.version += 1
                const { version } = this
                this.loading = true
                this.error = ''
                try {
                    const result = await this.$store.dispatch('tyManagement/loadProducts', page)
                    if (version === this.version && page > result.last_page) this.load(Math.max(1, result.last_page))
                } catch (error) {
                    if (version === this.version) this.error = errorMessage(error)
                } finally {
                    if (version === this.version) this.loading = false
                }
            },
        },
    }
</script>
